/* eslint-disable class-methods-use-this */
import React, {
    useReducer, useEffect, useCallback, useRef
} from 'react';
import { useDispatch } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    mediaQueries, modal, space, colors
} from 'style/config';
import {
    Grid, Link, Box, Icon
} from 'components/ui';
import Filter from 'components/ProductPage/Filters/Filter/Filter';
import ReviewsFiltersBreadcrumbs from 'components/ProductPage/RatingsAndReviews/ReviewsFilters/ReviewsFiltersBreadcrumbs';
import store from 'store/Store';
import ProductActions from 'actions/ProductActions';
import * as pdpAnalytics from 'analytics/productPage/pdpAnalytics';
import Pill from 'components/Pill';
import RadioFilter from 'components/ProductPage/Filters/FilterTypes/RadioFilter';
import ShadeFilter from 'components/ProductPage/Filters/FilterTypes/ShadeFilter';
import CheckboxImageFilter from 'components/ProductPage/Filters/FilterTypes/CheckboxImageFilter';
import Checkbox from 'components/Inputs/Checkbox/Checkbox';
import StarRating from 'components/StarRating/StarRating';
import skuUtils from 'utils/Sku';
import UI from 'utils/UI';
import TextInput from 'components/Inputs/TextInput/TextInput';
import NonIncentivizedTooltip from 'components/ProductPage/RatingsAndReviews/ReviewsFilters/NonIncentivizedTooltip';
import BeautyPreferencesFilter from 'components/ProductPage/RatingsAndReviews/ReviewsFilters/BeautyPreferencesFilter';
import userUtils from 'utils/User';
import productUtils from 'utils/product';
import localeUtils from 'utils/LanguageLocale';
import skuHelpers from 'utils/skuHelpers';
import ReviewsFiltersEN from 'components/ProductPage/RatingsAndReviews/ReviewsFilters/locales/ReviewsFilters_en_US';
import ReviewsFiltersFR from 'components/ProductPage/RatingsAndReviews/ReviewsFilters/locales/ReviewsFilters_fr_CA';

const getText = localeUtils.getLocalisedDataFromResource(ReviewsFiltersEN, ReviewsFiltersFR);
const FILTER_GAP = 6;
const AGE_RANGE_FILTER_ID = 'ageRange';
const AGE_RANGE_13_TO_17_FILTER_ID = '13-17';
const BP_FILTER_ID = 'beautyPreference';
const BP_FILTERS_IDS = [
    'skinType',
    'skinConcerns',
    'skinTone',
    'eyeColor',
    'ageRange',
    'hairColor',
    'hairType',
    'hairConcerns',
    'hairTexture',
    'fragrancePreferences'
];

const initialState = {
    selectedFilters: {},
    bvFilters: [],
    isSearch: false,
    selectedSentiment: '',
    bpFilterSelected: false
};

const reducer = (state, action) => {
    switch (action.type) {
        case 'SET_FILTERS':
            return {
                ...state,
                selectedFilters: action.filters,
                bpFilterSelected: action.bpSelected !== undefined ? action.bpSelected : state.bpFilterSelected
            };
        case 'RESET_FILTERS':
            return { ...state, selectedFilters: {}, bpFilterSelected: false };
        case 'SET_SEARCH':
            return { ...state, isSearch: action.value };
        case 'SET_SENTIMENT':
            return { ...state, selectedSentiment: action.value };
        default:
            return state;
    }
};

const ReviewsFilters = ({
    currentProduct = {}, productId, isSearchPerformed, loadInitialReviews, filtersConfiguration, categoryId
}) => {
    const skuAggregatedList = (currentProduct.regularChildSkus || []).concat(currentProduct.onSaleChildSkus || []);
    const [state, localDispatch] = useReducer(reducer, initialState);
    const reduxDispatch = useDispatch();
    const inputRef = useRef(null);
    const filterRef = useRef(null);
    const portalRef = useRef(null);
    const selectedSentimentRef = useRef('');
    const selectedFiltersRef = useRef(state.selectedFilters);
    selectedFiltersRef.current = state.selectedFilters;
    const bpFilterSelectedRef = useRef(state.bpFilterSelected);
    bpFilterSelectedRef.current = state.bpFilterSelected;
    const prevProductIdRef = useRef(productId);

    const fireAnalytics = useCallback((selectedFilters, isBPSelected = false) => {
        const selections = [];

        for (const key in selectedFilters) {
            if (Object.prototype.hasOwnProperty.call(selectedFilters, key)) {
                selections.push(key.replace(/([A-Z])/g, ' $1').toLowerCase() + '=' + selectedFilters[key].join(','));
            }
        }

        const filterSelections = isBPSelected ? 'your beauty preferences=Your Beauty Preferences' : selections.join('|');

        if (selections.length > 0) {
            pdpAnalytics.trackReviewFilterChange(filterSelections);
        }
    }, []);

    const applyFilters = useCallback(
        (filtersToApply = {}, scrollToFilters, isBPSelected = false) => {
            if (selectedSentimentRef.current !== '') {
                return;
            }

            const currentFilters = selectedFiltersRef.current;
            const newSelectedFilters = { ...currentFilters };

            for (const filterKey of Object.keys(filtersToApply)) {
                const filterValue = filtersToApply[filterKey];
                newSelectedFilters[filterKey] = filterValue;

                if (filterValue && !filterValue.length) {
                    delete newSelectedFilters[filterKey];
                }
            }

            if (JSON.stringify(newSelectedFilters) !== JSON.stringify(currentFilters)) {
                localDispatch({ type: 'SET_FILTERS', filters: newSelectedFilters, bpSelected: isBPSelected });
                reduxDispatch(ProductActions.applyReviewFilters({ ...newSelectedFilters }));

                if (scrollToFilters) {
                    UI.scrollTo({ ref: filterRef });
                }

                if (Object.keys(newSelectedFilters).length !== 0) {
                    fireAnalytics(newSelectedFilters, isBPSelected);
                }
            }
        },
        [reduxDispatch, fireAnalytics]
    );

    const applyBPFilters = useCallback(
        (filtersToApply = {}) => {
            const newFiltersToApply = { ...filtersToApply };

            if (bpFilterSelectedRef.current) {
                for (const filterKey of Object.keys(filtersToApply)) {
                    newFiltersToApply[filterKey] = [];
                }
            }

            applyFilters(newFiltersToApply, false, !bpFilterSelectedRef.current);
        },
        [applyFilters]
    );

    const resetFilters = useCallback(() => {
        localDispatch({ type: 'RESET_FILTERS' });
        reduxDispatch(ProductActions.applyReviewFilters({}));
    }, [reduxDispatch]);

    const removeFilter = useCallback(
        (filterKey, filterValue) => {
            const currentFilters = selectedFiltersRef.current;
            const newSelectedFilters = { ...currentFilters };
            newSelectedFilters[filterKey] = currentFilters[filterKey].filter(item => item !== filterValue);
            applyFilters(newSelectedFilters);
        },
        [applyFilters]
    );

    const fireEmptyBPAnalytics = useCallback(() => {
        const categoryName = productUtils.findProductRootParentCategoryName(currentProduct.parentCategory);
        pdpAnalytics.trackPageLoad({ pageName: `reviews:ratings&reviews-BP not saved:${categoryName.toLowerCase()}:*` });
    }, [currentProduct]);

    const handleSearchSubmit = useCallback(
        e => {
            e && e.preventDefault();
            const keyword = inputRef.current.getValue().trim();

            if (keyword === '') {
                return;
            }

            reduxDispatch(ProductActions.applyReviewsSearch(productId, keyword));
        },
        [productId, reduxDispatch]
    );

    useEffect(() => {
        const unsubscribeFilters = store.watchAction(ProductActions.TYPES.REVIEW_FILTERS_SELECTED, data => {
            if (data && data.filters) {
                applyFilters(data.filters, true);
            }
        });
        const unsubscribeUpdateHighlighted = store.watchAction(ProductActions.TYPES.UPDATE_HIGHLIGHTED_REVIEWS, data => {
            if (data && data.highlightedReviews) {
                selectedSentimentRef.current = data.highlightedReviews.selectedSentiment || '';
                localDispatch({ type: 'SET_SENTIMENT', value: selectedSentimentRef.current });
            }
        });
        const unsubscribeRemoveHighlighted = store.watchAction(ProductActions.TYPES.REMOVE_HIGHLIGHTED_REVIEWS, () => {
            selectedSentimentRef.current = '';
            localDispatch({ type: 'SET_SENTIMENT', value: '' });
        });

        return () => {
            unsubscribeFilters?.();
            unsubscribeUpdateHighlighted?.();
            unsubscribeRemoveHighlighted?.();
        };
    }, []);

    useEffect(() => {
        if ((isSearchPerformed && Object.keys(selectedFiltersRef.current).length !== 0) || productId !== prevProductIdRef.current) {
            resetFilters();
            localDispatch({ type: 'SET_SEARCH', value: false });

            if (inputRef.current) {
                inputRef.current.value = '';
            }
        }

        prevProductIdRef.current = productId;
    }, [isSearchPerformed, productId, resetFilters]);

    const renderTriggerPill = useCallback(
        ({ isActive, title, isSmall }) => (
            <Pill
                data-at={Sephora.debug.dataAt(`filter_pill${isSmall ? '_small' : ''}`)}
                isActive={isActive}
                fontSize='sm'
                hasArrow={true}
            >
                {title}
            </Pill>
        ),
        []
    );

    const renderContent = useCallback(
        (filterConfiguration, contentProps) => {
            const { isReviewsFiltersMasterListEnabled } = Sephora.configurationSettings;

            if (filterConfiguration.id === 'sku') {
                const { onClick, isSelected, isModal } = contentProps;
                const { regularChildSkus, swatchType } = currentProduct;
                const colorMatchSku = skuHelpers.getColorIQMatchSku(regularChildSkus);
                const showColorMatch = skuUtils.showColorIQOnPPage(currentProduct) ? colorMatchSku : false;
                const colorMatchSkuId = showColorMatch && showColorMatch.skuId;
                const hasScrollMenu = skuAggregatedList.length > 8;
                const filterList = skuAggregatedList.map(sku => {
                    const label = `${sku.variationValue || ''} ${sku.variationDesc ? `- ${sku.variationDesc}` : ''}`;

                    return (
                        <ShadeFilter
                            key={`${sku.skuId}_${sku.variationValue}`}
                            label={label}
                            value={sku.skuId}
                            image={sku.smallImage}
                            onClick={onClick}
                            sku={sku}
                            swatchType={swatchType}
                            isColorMatch={colorMatchSkuId === sku.skuId}
                            isSelected={isSelected(sku.skuId)}
                        />
                    );
                });

                return isModal ? (
                    filterList
                ) : (
                    <Box
                        {...(hasScrollMenu && {
                            maxHeight: 400,
                            overflowY: 'auto',
                            paddingBottom: 3,
                            marginBottom: -3,
                            marginRight: 2
                        })}
                    >
                        {filterList}
                    </Box>
                );
            }

            return filterConfiguration.options.map(option => {
                const { onClick, isSelected } = contentProps;
                const label =
                    filterConfiguration.optionType === 'star' ? (
                        <React.Fragment>
                            <StarRating
                                size='1em'
                                rating={parseInt(option.value)}
                            />
                            <span
                                css={styles.ratingLabel}
                                children={`${option.value} ${getText('star')}`}
                            />
                        </React.Fragment>
                    ) : (
                        option.value
                    );

                if (filterConfiguration.type === 'singleSelect') {
                    return (
                        <RadioFilter
                            key={option.value}
                            label={label}
                            value={option.value}
                            onClick={onClick}
                            isSelected={isSelected(option.value)}
                        />
                    );
                }

                if ((filterConfiguration.type === 'multiSelect' && filterConfiguration.optionType === 'withImage') || option.img) {
                    return (
                        <CheckboxImageFilter
                            key={option.value}
                            label={option.value}
                            value={option.value}
                            image={option.img || `/img/ufe/rich-profile/${filterConfiguration.id.toLowerCase()}-${option.bvValue.toLowerCase()}.png`}
                            onClick={onClick}
                            isSelected={isSelected(option.value)}
                            isRedesigned={isReviewsFiltersMasterListEnabled?.isEnabled}
                            hideIcon={option.hideIcon}
                        />
                    );
                }

                if (filterConfiguration.type === 'multiSelect') {
                    return (
                        <Checkbox
                            key={option.value}
                            checked={isSelected(option.value)}
                            value={option.value}
                            onClick={() => onClick(option.value)}
                            paddingY={3}
                            paddingX={[modal.paddingX[0], 4]}
                            baseCss={{
                                outline: 0,
                                transition: 'background-color .2s',
                                '.no-touch &:hover, :focus': { backgroundColor: colors.nearWhite }
                            }}
                            children={option.value}
                        />
                    );
                }

                return null;
            });
        },
        [currentProduct, skuAggregatedList]
    );

    const renderFilter = useCallback(
        filterConfiguration => {
            const filterId = filterConfiguration.id;
            const { selectedFilters, bpFilterSelected } = state;
            let filterIsSelected = !!selectedFilters[filterId];
            const isSingleSelect = filterConfiguration.type === 'singleSelect';
            const { isReviewsFiltersMasterListEnabled } = Sephora.configurationSettings;

            if (bpFilterSelected && BP_FILTERS_IDS.includes(filterId)) {
                filterIsSelected = false;
            }

            if (filterId === 'beautyMatches') {
                return null;
            }

            if (filterId === AGE_RANGE_FILTER_ID) {
                filterConfiguration.options = filterConfiguration.options.filter(opt => opt.value !== AGE_RANGE_13_TO_17_FILTER_ID);
            }

            if (isSingleSelect && filterConfiguration.options.length === 1) {
                const valueToApply = filterIsSelected ? [] : [filterConfiguration.options[0].value];

                return (
                    <Pill
                        data-at={Sephora.debug.dataAt('filter_pill')}
                        isActive={filterIsSelected}
                        fontSize='sm'
                        onClick={() => applyFilters({ [filterId]: valueToApply })}
                    >
                        {filterConfiguration.options[0].value}
                        {filterId === 'nonIncentivizedReviews' ? <NonIncentivizedTooltip isActive={filterIsSelected} /> : null}
                    </Pill>
                );
            }

            if (filterId === BP_FILTER_ID) {
                return (
                    <BeautyPreferencesFilter
                        filterIsSelected={bpFilterSelected}
                        filterId={BP_FILTER_ID}
                        categoryId={categoryId}
                        reviewFilters={filtersConfiguration}
                        applyFilters={applyBPFilters}
                        fireAnalytics={fireEmptyBPAnalytics}
                    />
                );
            }

            return (
                <Filter
                    {...(filterId === 'sku' && { width: 580 })}
                    id={`custom_${filterId}`}
                    name={filterId}
                    isSingleSelection={isSingleSelect}
                    title={filterConfiguration.label}
                    dropDownDataAt={'filter_menu'}
                    applyFilters={applyFilters}
                    trigger={renderTriggerPill}
                    selected={selectedFilters[filterId]}
                    bpFilterActive={bpFilterSelected}
                    hasCustomScroll={filterId === 'sku' && skuAggregatedList.length > 8}
                    content={props_ => renderContent(filterConfiguration, props_)}
                    isRedesigned={isReviewsFiltersMasterListEnabled?.isEnabled}
                />
            );
        },
        [
            state,
            applyFilters,
            applyBPFilters,
            renderContent,
            renderTriggerPill,
            fireEmptyBPAnalytics,
            categoryId,
            filtersConfiguration,
            skuAggregatedList
        ]
    );

    const { selectedFilters } = state;
    const { isReviewsFiltersMasterListEnabled } = Sephora.configurationSettings;

    if (isReviewsFiltersMasterListEnabled?.isEnabled && userUtils.isSignedIn()) {
        const isBPFilterPresent = filtersConfiguration.some(filter => filter.id === BP_FILTER_ID);
        const targetPosition = filtersConfiguration.findIndex(filter => filter.id === 'nonIncentivizedReviews');

        if (!isBPFilterPresent) {
            filtersConfiguration.splice(targetPosition + 1, 0, { id: BP_FILTER_ID });
        }
    }

    const showFilters = !!(!state.isSearch && !isSearchPerformed);

    return (
        <Box
            id='custom_filters'
            marginTop={[4, 6, 7]}
        >
            <div ref={portalRef} />
            <div
                css={styles.filtersWrap}
                ref={filterRef}
            >
                {showFilters ? (
                    <>
                        <div css={styles.filter}>
                            <Pill
                                data-at={Sephora.debug.dataAt('filter_pill')}
                                fontSize='md'
                                paddingX={null}
                                width={36}
                                onClick={() => {
                                    localDispatch({ type: 'SET_SEARCH', value: true });
                                    setTimeout(() => inputRef.current?.focus(), 0);
                                }}
                            >
                                <Icon
                                    name='search'
                                    size='1em'
                                />
                            </Pill>
                        </div>
                        {filtersConfiguration &&
                            filtersConfiguration.map(configuration => {
                                if (
                                    ((!configuration.options || configuration.options.length === 0) &&
                                        configuration.id !== 'sku' &&
                                        configuration.id !== BP_FILTER_ID &&
                                        configuration.id !== 'beautyMatches') ||
                                    (configuration.id === 'sku' &&
                                        !(
                                            currentProduct.variationType === skuUtils.skuVariationType.COLOR &&
                                            skuAggregatedList.length > 0 &&
                                            currentProduct.skuSelectorType !== skuUtils.skuSwatchType.NONE
                                        ))
                                ) {
                                    return null;
                                }

                                return (
                                    <div
                                        key={configuration.id}
                                        css={styles.filter}
                                    >
                                        {renderFilter(configuration)}
                                    </div>
                                );
                            })}
                    </>
                ) : null}
                {!showFilters && (
                    <Grid
                        is='form'
                        gap={5}
                        paddingX={[2, 0]}
                        columns={['1fr auto', '415px auto']}
                        alignItems='center'
                        marginTop={`${FILTER_GAP}px`}
                        onSubmit={handleSearchSubmit}
                    >
                        <TextInput
                            type='search'
                            autoOff={true}
                            name='keyword'
                            id='rr_search_input'
                            maxLength={70}
                            placeholder={getText('searchKeyword')}
                            ref={inputRef}
                            isSmall={true}
                            marginBottom={null}
                            value={(inputRef.current && inputRef.current.getValue()) || ''}
                            contentAfter={
                                <button
                                    css={styles.clearButton}
                                    type='button'
                                    data-at={Sephora.debug.dataAt('rr_search_icon_cross')}
                                    onClick={() => {
                                        inputRef.current.empty();
                                        inputRef.current.focus();
                                    }}
                                >
                                    <Icon
                                        name='x'
                                        size={8}
                                    />
                                </button>
                            }
                        />
                        <div>
                            <Link
                                color='blue'
                                padding={2}
                                margin={-2}
                                data-at={Sephora.debug.dataAt('rr_search_cancel')}
                                onClick={() => {
                                    localDispatch({ type: 'SET_SEARCH', value: false });

                                    if (isSearchPerformed) {
                                        loadInitialReviews();
                                    }
                                }}
                                children={getText('cancel')}
                            />
                        </div>
                    </Grid>
                )}
            </div>
            <ReviewsFiltersBreadcrumbs
                onRemoveChiclet={removeFilter}
                onClearAllFilters={resetFilters}
                skuAggregatedList={skuAggregatedList}
                selectedFilters={selectedFilters}
            />
        </Box>
    );
};

const styles = {
    filtersWrap: {
        marginTop: -FILTER_GAP,
        [mediaQueries.xsMax]: {
            whiteSpace: 'nowrap',
            marginLeft: -space.container,
            marginRight: -space.container,
            paddingLeft: space[2],
            paddingRight: space[2],
            overflowX: 'auto',
            paddingBottom: 3,
            marginBottom: -3,
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' }
        },
        [mediaQueries.sm]: { display: 'flex', flexWrap: 'wrap' }
    },
    clearButton: {
        color: colors.white,
        backgroundColor: colors.gray,
        lineHeight: 0,
        borderRadius: 99999,
        width: 16,
        height: 16,
        marginRight: space[2],
        alignSelf: 'center',
        textAlign: 'center',
        '.no-touch &:hover': { backgroundColor: colors.black }
    },
    filter: {
        display: 'inline-block',
        verticalAlign: 'top',
        marginTop: FILTER_GAP,
        marginRight: FILTER_GAP,
        '&:last-child': { marginRight: 0 }
    },
    ratingLabel: { marginLeft: '.7em', marginTop: '.125em', lineHeight: 0 }
};

export default wrapFunctionalComponent(ReviewsFilters, 'ReviewsFilters');
